import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/lib/api-client";

export interface UserMe {
  id: string;
  auth_id: string;
  email: string;
  display_name: string | null;
  plan: string;
  digest_frequency: string;
  concept_count: number;
  // Optional: older cached shapes (pre-ring-rollout) lack this field.
  tags?: string[];
  features?: { setup_v2: boolean; facets?: boolean; durable_jobs?: boolean };
  plan_limits: {
    max_concepts: number | null;
    scoring_per_concept: number | null;
    field_debate_trials: number | null;
    gap_analysis_trials: number | null;
    chat_messages_per_month: number | null;
    max_facets?: number | null;
  };
  usage: {
    concept_count: number;
    field_debate_trials_used: number;
    gap_analysis_trials_used: number;
    chat_messages_this_month: number;
  };
  created_at: string;
  updated_at: string;
}

export function useCurrentUser() {
  return useQuery<UserMe>({
    queryKey: ["user", "me"],
    queryFn: () => apiClient.get("api/users/me").json(),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

export function isPaidPlan(plan: string | undefined): boolean {
  return !!plan && plan !== "free";
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { digest_frequency: string }) =>
      apiClient.patch("api/users/me", { json: data }),
    onMutate: async (data) => {
      await queryClient.cancelQueries({ queryKey: ["user", "me"] });
      const prev = queryClient.getQueryData<UserMe>(["user", "me"]);
      if (prev) {
        queryClient.setQueryData<UserMe>(["user", "me"], { ...prev, ...data });
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(["user", "me"], ctx.prev);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["user", "me"] }),
  });
}
